import { Injectable, signal, computed, effect } from '@angular/core';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MenuCollapseService {

  private _isStaticMenu = signal<boolean>(false);
  readonly isStaticMenu = this._isStaticMenu.asReadonly();

  private _isHamburgerMenu = signal<boolean>(false);
  readonly isHamburgerMenu = this._isHamburgerMenu.asReadonly();

  private _isDynamicMenu = signal<boolean>(false);
  readonly isDynamicMenu = this._isDynamicMenu.asReadonly();

  private _isMobileSearchHidden = signal<boolean>(false);
  readonly isMobileSearchHidden = this._isMobileSearchHidden.asReadonly();

  // Computed signals for derived state
  readonly isAnyMenuOpen = computed(() =>
    this._isStaticMenu() || this._isHamburgerMenu() || this._isDynamicMenu()
  );

  readonly isMobileView = computed(() =>
    this._isHamburgerMenu() || this._isMobileSearchHidden()
  );

  // Observables for backward compatibility
  private _staticMenuSubject = new Subject<boolean>();
  readonly isStaticMenu$: Observable<boolean> = this._staticMenuSubject.asObservable();

  private _hamburgerMenuSubject = new Subject<boolean>();
  readonly isHamburgerMenu$: Observable<boolean> = this._hamburgerMenuSubject.asObservable();

  private _dynamicMenuSubject = new Subject<boolean>();
  readonly isDynamicMenu$: Observable<boolean> = this._dynamicMenuSubject.asObservable();

  private _mobileSearchHiddenSubject = new Subject<boolean>();
  readonly isMobileSearchHidden$: Observable<boolean> = this._mobileSearchHiddenSubject.asObservable();

  constructor() {
    // Effects to sync signal changes to Observables
    effect(() => {
      this._staticMenuSubject.next(this._isStaticMenu());
    });

    effect(() => {
      this._hamburgerMenuSubject.next(this._isHamburgerMenu());
    });

    effect(() => {
      this._dynamicMenuSubject.next(this._isDynamicMenu());
    });

    effect(() => {
      this._mobileSearchHiddenSubject.next(this._isMobileSearchHidden());
    });
  }

  // Setter methods
  setStaticMenu(state: boolean) {
    this._isStaticMenu.set(state);
  }

  setHamburgerMenu(state: boolean) {
    this._isHamburgerMenu.set(state);
  }

  setDynamicMenu(state: boolean) {
    this._isDynamicMenu.set(state);
  }

  setMobileSearchHidden(state: boolean) {
    this._isMobileSearchHidden.set(state);
  }

  // Convenience methods
  toggleStaticMenu() {
    this._isStaticMenu.update(current => !current);
  }

  toggleHamburgerMenu() {
    this._isHamburgerMenu.update(current => !current);
  }

  toggleDynamicMenu() {
    this._isDynamicMenu.update(current => !current);
  }

  toggleMobileSearchHidden() {
    this._isMobileSearchHidden.update(current => !current);
  }

  // Close all menus
  closeAllMenus() {
    this._isStaticMenu.set(false);
    this._isHamburgerMenu.set(false);
    this._isDynamicMenu.set(false);
  }
}
